import { logger } from '@/utils/logger';
import { toast } from "sonner";
import { endSession, isSessionEnding } from '@/lib/session';

const API_BASE_URL = import.meta.env.VITE_API_URL;
const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const RATE_LIMIT_WINDOW = 1000;
const MAX_REQUESTS_PER_WINDOW = 2;
const MAX_RETRIES = 3;
const RETRY_DELAY = 1000;
const CACHE_TTL = 5000;
const VIDEOS_CACHE_TTL = 10000;
const FLEET_CACHE_TTL = 0;

const requestTimestamps: Record<string, number[]> = {};// Use unknown instead of any for stronger typing at call sites
const inFlightRequests: Record<string, Promise<unknown>> = {};
const responseCache: Record<string, { data: unknown; timestamp: number }> = {};
const debounceMap = new Map<string, ReturnType<typeof setTimeout>>();

const debounce = <T>(key: string, fn: () => Promise<T>, wait: number): Promise<T> => {
  return new Promise<T>((resolve, reject) => {
    if (debounceMap.has(key)) {
      clearTimeout(debounceMap.get(key));

      const cached = responseCache[key];
      if (cached) {
        resolve(cached.data as T);
        return;
      }
    }

    const timeout = setTimeout(() => {
      debounceMap.delete(key);
      fn().then(resolve).catch(reject);
    }, wait);

    debounceMap.set(key, timeout);
  });
};

type QueuedRequest = {
  key: string;
  execute: () => Promise<unknown>;
  resolve: (value: unknown) => void;
  reject: (reason: unknown) => void;
};
const requestQueue: QueuedRequest[] = [];
let processingQueue = false;

const isValidUUID = (id: string): boolean => {
  return UUID_REGEX.test(id);
};

const getRequestKey = (method: string, endpoint: string, params?: Record<string, unknown>): string => {
  if (method.toLowerCase() === 'get' && params) {
    return `${method}:${endpoint}:${JSON.stringify(params)}`;
  }
  return `${method}:${endpoint}`;
};

const processQueue = async () => {
  if (processingQueue || requestQueue.length === 0) return;

  processingQueue = true;

  try {
    const request = requestQueue.shift();
    if (!request) {
      processingQueue = false;
      return;
    }

    try {
      const response = await request.execute();
      request.resolve(response);
    } catch (error) {
      request.reject(error);
    } finally {
      processingQueue = false;
      setTimeout(processQueue, 50);
    }
  } catch (error) {
    logger.error('Error processing request queue:', error);
    processingQueue = false;
    setTimeout(processQueue, 100);
  }
};

const enqueueRequest = <T>(key: string, execute: () => Promise<T>): Promise<T> => {
  return new Promise<T>((resolve, reject) => {
    const cacheTtl = key.includes('/v1/fleet/')
      ? FLEET_CACHE_TTL
      : key.includes('/v1/videos') && !key.includes('/v1/videos/')
        ? VIDEOS_CACHE_TTL
        : CACHE_TTL;

    const cached = responseCache[key];
    if (cached && Date.now() - cached.timestamp < cacheTtl) {
      return resolve(cached.data as T);
    }

    if (inFlightRequests[key]) {
      return (inFlightRequests[key] as Promise<T>).then(resolve).catch(reject);
    }

    const promise: Promise<T> = new Promise<T>((innerResolve, innerReject) => {
      requestQueue.push({
        key,
        execute: async () => {
          try {
            const endpointKey = key.split(':')[1] || key;
            if (!requestTimestamps[endpointKey]) {
              requestTimestamps[endpointKey] = [];
            }

            const now = Date.now();
            const timestamps = requestTimestamps[endpointKey];
            const windowStart = now - RATE_LIMIT_WINDOW;
            const recentRequests = timestamps.filter(timestamp => timestamp >= windowStart);
            requestTimestamps[endpointKey] = recentRequests;

            if (recentRequests.length >= MAX_REQUESTS_PER_WINDOW) {
              const oldestAllowed = now - RATE_LIMIT_WINDOW;
              const sortedTimestamps = [...recentRequests].sort((a, b) => a - b);
              const oldestInWindow = sortedTimestamps[sortedTimestamps.length - MAX_REQUESTS_PER_WINDOW];

              if (oldestInWindow >= oldestAllowed) {
                const delayTime = oldestInWindow + RATE_LIMIT_WINDOW - now + 100;
                await delay(delayTime);
              }
            }

            requestTimestamps[endpointKey].push(Date.now());
            const result = await execute();
            responseCache[key] = {
              data: result,
              timestamp: Date.now()
            };

            return result;
          } finally {
            delete inFlightRequests[key];
          }
        },
        resolve: innerResolve,
        reject: innerReject
      });

      if (!processingQueue) {
        processQueue();
      }
    });

    inFlightRequests[key] = promise;
    promise.then(resolve).catch(reject);
  });
};

const validateUUIDInEndpoint = (endpoint: string): boolean => {
  const uuidPatterns = [
    /\/v1\/videos\/([^/]+)(?:\/|$)/,
    /\/v1\/r\/(?:thumbnail|processed|flow)\/([^/]+)(?:\/|$)/,
  ];

  for (const pattern of uuidPatterns) {
    const match = endpoint.match(pattern);
    if (match && match[1]) {
      const possibleUuid = match[1];
      if (!isValidUUID(possibleUuid)) {
        logger.error(`Invalid UUID in endpoint: ${possibleUuid}`);
        return false;
      }
    }
  }

  return true;
};

const delay = (ms: number): Promise<void> => {
  return new Promise(resolve => setTimeout(resolve, ms));
};

const lastExecutionTime: Record<string, number> = {};
const throttle = <T>(key: string, fn: () => Promise<T>, minInterval: number): Promise<T> => {
  return new Promise((resolve, reject) => {
    const now = Date.now();
    const lastTime = lastExecutionTime[key] || 0;
    const elapsed = now - lastTime;

    if (elapsed < minInterval) {
      const waitTime = minInterval - elapsed;
      delay(waitTime)
        .then(() => fn().then(resolve).catch(reject))
        .catch(reject);
      return;
    }

    lastExecutionTime[key] = Date.now();
    fn().then(resolve).catch(reject);
  });
};

const getAuthHeaders = () => {
  const token = localStorage.getItem('auth_token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {})
  };
};

let refreshPromise: Promise<boolean> | null = null;

export const refreshAccessToken = (): Promise<boolean> => {
  if (refreshPromise) {
    return refreshPromise;
  }

  const refreshToken = localStorage.getItem('refresh_token');
  if (!refreshToken) {
    return Promise.resolve(false);
  }

  refreshPromise = (async (): Promise<boolean> => {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/auth/refresh_token`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${refreshToken}`,
        },
      });

      if (!response.ok) {
        return false;
      }

      const data = await response.json().catch(() => null);
      const newAccessToken = data?.access_token;
      if (!newAccessToken) {
        return false;
      }

      localStorage.setItem('auth_token', newAccessToken);
      return true;
    } catch (error) {
      logger.error('Token refresh failed:', error instanceof Error ? error : undefined);
      return false;
    }
  })().finally(() => {
    refreshPromise = null;
  });

  return refreshPromise;
};

const toResponseError = (response: Response, data: unknown) => ({
  response: {
    status: response.status,
    statusText: response.statusText,
    headers: response.headers,
    url: response.url,
    data,
  },
});

const handleTokenExpiration = () => {
  endSession('expired');
};

const handleApiError = (error: unknown) => {
  let errorMessage = 'An unexpected error occurred';

  if (typeof error === 'object' && error !== null && 'response' in error) {
    const typedError = error as { response?: { status?: number; url?: string; data?: { message?: string; detail?: string } }; request?: unknown };
    const status = typedError.response?.status;

    logger.error('API Error:', undefined, {
      status,
      url: typedError.response?.url,
      data: typedError.response?.data,
    });

    if (status === 401) {
      handleTokenExpiration();
      return Promise.reject(new Error("Session expired"));
    } else if (status === 403) {
      errorMessage = 'You do not have permission to perform this action';
    } else if (status === 404) {
      errorMessage = 'The requested resource was not found';
    } else if (status === 413) {
      return Promise.reject(error);
    } else if (status === 429) {
      errorMessage = 'Too many requests. Please try again in a moment.';
      return Promise.reject(error);
    } else if (typedError.response?.data?.message) {
      errorMessage = typedError.response.data.message;
    }
  } else if (typeof error === 'object' && error !== null && 'request' in error) {
    logger.error('API Error: network failure', error instanceof Error ? error : undefined);
    errorMessage = 'Network error. Please check your connection.';
  } else {
    logger.error('API Error:', error instanceof Error ? error : undefined);
  }

  if (isSessionEnding()) {
    return Promise.reject(error);
  }

  toast.error(errorMessage);
  return Promise.reject(error);
};

const executeFetchWithRetries = async (
  fetchFn: () => Promise<Response>,
  options: { maxRetries?: number; allowRefresh?: boolean } = {}
): Promise<Response> => {
  const { maxRetries = MAX_RETRIES, allowRefresh = true } = options;
  let retries = 0;
  let triedRefresh = false;

  while (true) {
    try {
      const response = await fetchFn();
      if (response.status === 429 && retries < maxRetries) {
        retries++;
        const retryDelay = RETRY_DELAY * Math.pow(2, retries - 1);
        await delay(retryDelay);
        continue;
      }

      if (response.status === 401 && allowRefresh && !triedRefresh) {
        triedRefresh = true;
        const refreshed = await refreshAccessToken();
        if (refreshed) {
          continue;
        }
      }

      return response;
    } catch (error: unknown) {
      if (error instanceof TypeError && retries < maxRetries) {
        retries++;
        const retryDelay = RETRY_DELAY * Math.pow(2, retries - 1);
        await delay(retryDelay);
        continue;
      }

      throw error;
    }
  }
};

export const apiClient = {
  clearCache(endpointPrefix?: string) {
    if (!endpointPrefix) {
      Object.keys(responseCache).forEach((k) => delete responseCache[k]);
      return;
    }
    Object.keys(responseCache).forEach((k) => {
      if (k.includes(endpointPrefix)) {
        delete responseCache[k];
      }
    });
  },

  async get(endpoint: string, params: Record<string, string | number | boolean | null> = {}, options?: { suppressToast?: boolean; treat404AsNull?: boolean }) {
    if (isSessionEnding() && !endpoint.startsWith('/v1/auth')) {
      throw new Error('Redirecting to login');
    }
    const url = new URL(`${API_BASE_URL}${endpoint}`);
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        url.searchParams.append(key, String(value));
      }
    });

    if (!validateUUIDInEndpoint(endpoint)) {
      throw new Error("Invalid UUID in endpoint");
    }

    const requestKey = getRequestKey('GET', endpoint, params);

    try {
      const isVideosEndpoint = endpoint === '/v1/videos';

      const executeRequest = async () => {
        return await enqueueRequest(requestKey, async () => {
          const response = await executeFetchWithRetries(async () => {
            return fetch(url.toString(), {
              method: 'GET',
              headers: getAuthHeaders(),
              redirect: 'manual',
            });
          }, { allowRefresh: !endpoint.startsWith('/v1/auth') });

          if ((response as Response & { type?: string }).type === 'opaqueredirect' || response.status === 0) {
            if (/\/v1\/r\/(?:thumbnail|processed|flow)\//.test(endpoint)) {
              return { _redirect_failed: true } as { _redirect_failed: boolean };
            }
          }

          if (response.status >= 300 && response.status < 400 && response.headers.has('location')) {
            const location = response.headers.get('location');
            if (location) {
              if (/\/v1\/r\/(?:thumbnail|processed|flow)\//.test(endpoint)) {
                return { _redirect_failed: true, url: location } as { _redirect_failed: boolean; url: string };
              }
              try {
                const redirectedResponse = await fetch(location, { method: 'GET' });
                if (redirectedResponse.ok) {
                  const ct = redirectedResponse.headers.get('content-type') || '';
                  if (ct.includes('application/json')) {
                    return await redirectedResponse.json();
                  }
                  return { url: location } as { url: string };
                }
              } catch {
                // ignore redirect follow errors and fall through
              }
              throw new Error(`Failed to fetch redirected resource`);
            } else {
              if (/\/v1\/r\/(?:thumbnail|processed|flow)\//.test(endpoint)) {
                return { _redirect_failed: true } as { _redirect_failed: boolean };
              }
              throw new Error('Redirect location not found');
            }
          }

          if (!response.ok) {
            let responseData;
            try {
              responseData = await response.json();
            } catch {
              try {
                responseData = { message: await response.text() || 'An error occurred' };
              } catch {
                responseData = { message: 'An error occurred' };
              }
            }

            if (response.status === 401 && !endpoint.startsWith('/v1/auth')) {
              handleTokenExpiration();
              throw new Error("Session expired");
            }

            throw toResponseError(response, responseData);
          }

          return await response.json();
        });
      };

      if (isVideosEndpoint) {
        return await throttle(`videos-fetch`, executeRequest, 500);
      } else {
        return await executeRequest();
      }
    } catch (error: unknown) {
      if (typeof error === 'object' && error !== null && 'response' in error) {
        const status = (error as { response?: { status?: number } }).response?.status;
        if (status === 401) {
          handleTokenExpiration();
          return Promise.reject(new Error("Session expired"));
        }
      }
      if (options?.treat404AsNull && typeof error === 'object' && error !== null && 'response' in error && (error as { response?: { status?: number } }).response?.status === 404) {
        return null as unknown;
      }
      if (options?.suppressToast) {
        return Promise.reject(error);
      }
      return handleApiError(error);
    }
  },

  async post(endpoint: string, data: unknown = {}) {
    if (isSessionEnding() && !endpoint.startsWith('/v1/auth')) {
      throw new Error('Redirecting to login');
    }
    if (!validateUUIDInEndpoint(endpoint)) {
      throw new Error("Invalid UUID in endpoint");
    }
    const requestKey = getRequestKey('POST', endpoint);

    try {
      return await enqueueRequest(requestKey, async () => {
        const response = await executeFetchWithRetries(async () => {
          return fetch(`${API_BASE_URL}${endpoint}`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify(data),
          });
        }, { allowRefresh: !endpoint.startsWith('/v1/auth') });

        if (!response.ok) {
          let responseData;
          try {
            responseData = await response.json();
          } catch {
            try {
              responseData = { message: await response.text() || 'An error occurred' };
            } catch {
              responseData = { message: 'An error occurred' };
            }
          }

          if (response.status === 401 && !endpoint.startsWith('/v1/auth')) {
            handleTokenExpiration();
            throw new Error("Session expired");
          }

          throw toResponseError(response, responseData);
        }

        return await response.json();
      });
    } catch (error) {
      return handleApiError(error);
    }
  },

  async put(endpoint: string, data: unknown = {}) {
    if (isSessionEnding() && !endpoint.startsWith('/v1/auth')) {
      throw new Error('Redirecting to login');
    }
    if (!validateUUIDInEndpoint(endpoint)) {
      throw new Error("Invalid UUID in endpoint");
    }

    const requestKey = getRequestKey('PUT', endpoint);

    try {
      return await enqueueRequest(requestKey, async () => {
        const response = await executeFetchWithRetries(async () => {
          return fetch(`${API_BASE_URL}${endpoint}`, {
            method: 'PUT',
            headers: getAuthHeaders(),
            body: JSON.stringify(data),
          });
        }, { allowRefresh: !endpoint.startsWith('/v1/auth') });

        if (!response.ok) {
          let responseData;
          try {
            responseData = await response.json();
          } catch {
            try {
              responseData = { message: await response.text() || 'An error occurred' };
            } catch {
              responseData = { message: 'An error occurred' };
            }
          }

          if (response.status === 401 && !endpoint.startsWith('/v1/auth')) {
            handleTokenExpiration();
            throw new Error("Session expired");
          }

          throw toResponseError(response, responseData);
        }

        return await response.json();
      });
    } catch (error) {
      return handleApiError(error);
    }
  },

  async patch(endpoint: string, data: unknown = {}) {
    if (isSessionEnding() && !endpoint.startsWith('/v1/auth')) {
      throw new Error('Redirecting to login');
    }
    if (!validateUUIDInEndpoint(endpoint)) {
      throw new Error("Invalid UUID in endpoint");
    }

    const requestKey = getRequestKey('PATCH', endpoint);

    try {
      return await enqueueRequest(requestKey, async () => {
        const response = await executeFetchWithRetries(async () => {
          return fetch(`${API_BASE_URL}${endpoint}`, {
            method: 'PATCH',
            headers: getAuthHeaders(),
            body: JSON.stringify(data),
          });
        }, { allowRefresh: !endpoint.startsWith('/v1/auth') });

        if (!response.ok) {
          let responseData;
          try {
            responseData = await response.json();
          } catch {
            try {
              responseData = { message: await response.text() || 'An error occurred' };
            } catch {
              responseData = { message: 'An error occurred' };
            }
          }

          if (response.status === 401 && !endpoint.startsWith('/v1/auth')) {
            handleTokenExpiration();
            throw new Error("Session expired");
          }

          throw toResponseError(response, responseData);
        }

        return await response.json();
      });
    } catch (error) {
      return handleApiError(error);
    }
  },

  async delete(endpoint: string) {
    if (isSessionEnding() && !endpoint.startsWith('/v1/auth')) {
      throw new Error('Redirecting to login');
    }
    if (!validateUUIDInEndpoint(endpoint)) {
      throw new Error("Invalid UUID in endpoint");
    }
    const requestKey = getRequestKey('DELETE', endpoint);

    try {
      return await enqueueRequest(requestKey, async () => {
        const response = await executeFetchWithRetries(async () => {
          return fetch(`${API_BASE_URL}${endpoint}`, {
            method: 'DELETE',
            headers: getAuthHeaders(),
          });
        }, { allowRefresh: !endpoint.startsWith('/v1/auth') });

        if (!response.ok) {
          let responseData;
          try {
            responseData = await response.json();
          } catch {
            try {
              responseData = { message: await response.text() || 'An error occurred' };
            } catch {
              responseData = { message: 'An error occurred' };
            }
          }

          if (response.status === 401 && !endpoint.startsWith('/v1/auth')) {
            handleTokenExpiration();
            throw new Error("Session expired");
          }

          throw toResponseError(response, responseData);
        }
        if (response.status === 204) {
          return undefined;
        }

        return await response.json();
      });
    } catch (error) {
      return handleApiError(error);
    }
  },

  async uploadFile(endpoint: string, file: File, onProgress?: (percentage: number) => void) {
    if (isSessionEnding() && !endpoint.startsWith('/v1/auth')) {
      throw new Error('Redirecting to login');
    }
    if (!validateUUIDInEndpoint(endpoint)) {
      throw new Error("Invalid UUID in endpoint");
    }

    const requestKey = getRequestKey('UPLOAD', endpoint, { fileName: file.name });

    try {
      return await enqueueRequest(requestKey, async () => {
        const sendUpload = () => new Promise<{ ok: boolean; status: number; data: unknown }>((resolve, reject) => {
          const formData = new FormData();
          formData.append('file', file);

          const token = localStorage.getItem('auth_token');

          const xhr = new XMLHttpRequest();
          xhr.open('POST', `${API_BASE_URL}${endpoint}`, true);

          if (token) {
            xhr.setRequestHeader('Authorization', `Bearer ${token}`);
          }

          if (onProgress) {
            xhr.upload.onprogress = (event) => {
              if (event.lengthComputable) {
                const pct = Math.min(99, Math.round((event.loaded / event.total) * 100));
                onProgress(pct);
              }
            };
          }

          xhr.onload = () => {
            let data: unknown;
            try {
              data = xhr.responseText ? JSON.parse(xhr.responseText) : null;
            } catch {
              data = { message: xhr.responseText || 'An error occurred' };
            }
            if (onProgress && xhr.status >= 200 && xhr.status < 300) {
              onProgress(100);
            }
            resolve({ ok: xhr.status >= 200 && xhr.status < 300, status: xhr.status, data });
          };

          xhr.onerror = () => reject(new Error('Network error during upload'));
          xhr.onabort = () => reject(new Error('Upload aborted'));

          xhr.send(formData);
        });

        let xhrResult = await sendUpload();

        if (!xhrResult.ok && xhrResult.status === 401 && !endpoint.startsWith('/v1/auth')) {
          const refreshed = await refreshAccessToken();
          if (refreshed) {
            if (onProgress) {
              onProgress(0);
            }
            xhrResult = await sendUpload();
          }
        }

        if (!xhrResult.ok) {
          if (xhrResult.status === 401) {
            handleTokenExpiration();
            throw new Error('Session expired');
          }
          throw { response: { status: xhrResult.status, data: xhrResult.data } };
        }

        return xhrResult.data;
      });
    } catch (error) {
      return handleApiError(error);
    }
  },

  async postFormData(endpoint: string, formData: FormData) {
    if (isSessionEnding() && !endpoint.startsWith('/v1/auth')) {
      throw new Error('Redirecting to login');
    }
    if (!validateUUIDInEndpoint(endpoint)) {
      throw new Error("Invalid UUID in endpoint");
    }

    const requestKey = getRequestKey('POST_FORMDATA', endpoint);

    try {
      return await enqueueRequest(requestKey, async () => {
        const response = await executeFetchWithRetries(async () => {
          const token = localStorage.getItem('auth_token');
          const headers: Record<string, string> = {};

          if (token) {
            headers['Authorization'] = `Bearer ${token}`;
          }

          return fetch(`${API_BASE_URL}${endpoint}`, {
            method: 'POST',
            headers,
            body: formData,
          });
        }, { allowRefresh: !endpoint.startsWith('/v1/auth') });

        if (!response.ok) {
          let responseData;
          try {
            responseData = await response.json();
          } catch {
            try {
              responseData = { message: await response.text() || 'An error occurred' };
            } catch {
              responseData = { message: 'An error occurred' };
            }
          }

          if (response.status === 401 && !endpoint.startsWith('/v1/auth')) {
            handleTokenExpiration();
            throw new Error("Session expired");
          }

          throw toResponseError(response, responseData);
        }

        return await response.json();
      });
    } catch (error) {
      return handleApiError(error);
    }
  }
};